import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Public persona chat for the standalone FastResearch flow.
// Backend: GET /fast-research/:id/chat, POST /fast-research/:id/chat/message.
// Access is gated server-side (free until progress.pdf=complete or createdAt+15min,
// paid until personaChat.accessExpiresAt). This component renders a blocked state
// when the server says access is not allowed.

const ACCESS_POLL_MS = 30000;
const DESKTOP_MIN_WIDTH = 960;

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);
    const handler = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isDesktop;
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    overflow: 'hidden',
    color: '#fff',
    fontFamily: 'inherit'
  },
  participantsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    flexShrink: 0,
    paddingRight: '0.5rem'
  },
  participants: {
    display: 'flex',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    overflowX: 'auto',
    flex: 1,
    minWidth: 0,
    scrollbarWidth: 'thin'
  },
  settingsButton: {
    flexShrink: 0,
    width: '34px',
    height: '34px',
    borderRadius: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.12s, border-color 0.12s'
  },
  avatar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
    minWidth: '56px',
    flexShrink: 0
  },
  avatarImgWrap: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#fff',
    border: '2px solid'
  },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  avatarName: {
    fontSize: '0.65rem',
    maxWidth: '68px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    opacity: 0.7
  },
  messages: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    scrollBehavior: 'smooth'
  },
  empty: {
    margin: 'auto',
    textAlign: 'center',
    fontSize: '0.9rem',
    opacity: 0.6,
    maxWidth: '280px',
    lineHeight: 1.5
  },
  userRow: { display: 'flex', justifyContent: 'flex-end' },
  personaRow: { display: 'flex', gap: '0.5rem', alignItems: 'flex-start' },
  userBubble: {
    backgroundColor: 'rgba(99, 102, 241, 0.25)',
    border: '1px solid rgba(99, 102, 241, 0.35)',
    borderRadius: '14px 14px 2px 14px',
    padding: '0.5rem 0.85rem',
    maxWidth: '82%',
    fontSize: '0.9rem',
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  personaBubble: {
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    borderRadius: '2px 14px 14px 14px',
    padding: '0.5rem 0.85rem',
    maxWidth: '82%',
    fontSize: '0.9rem',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  personaSmallAvatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    flexShrink: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#fff'
  },
  personaMeta: { display: 'flex', flexDirection: 'column', gap: '0.15rem', flex: 1, minWidth: 0 },
  personaName: { fontSize: '0.7rem', opacity: 0.6, paddingLeft: '0.25rem' },
  typingText: {
    padding: '0.25rem 0',
    fontSize: '0.8rem',
    opacity: 0.55,
    fontStyle: 'italic'
  },
  cursor: {
    display: 'inline-block',
    marginLeft: '2px',
    color: '#a78bfa',
    opacity: 0.7,
    transform: 'translateY(-1px)'
  },
  inputRow: {
    display: 'flex',
    gap: '0.5rem',
    padding: '0.75rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    flexShrink: 0
  },
  textarea: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px',
    color: '#fff',
    padding: '0.5rem 0.75rem',
    fontSize: '0.9rem',
    lineHeight: 1.35,
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    height: '38px',
    minHeight: '38px',
    maxHeight: '88px',
    boxSizing: 'border-box'
  },
  sendBtn: {
    backgroundColor: '#6366f1',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    width: '44px',
    minWidth: '44px',
    fontSize: '1.1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s'
  },
  sendBtnDisabled: { backgroundColor: 'rgba(99, 102, 241, 0.35)', cursor: 'not-allowed' },
  blocked: {
    padding: '1rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    fontSize: '0.85rem',
    textAlign: 'center',
    opacity: 0.75,
    flexShrink: 0,
    backgroundColor: 'rgba(255,255,255,0.02)'
  },
  blockedAction: {
    display: 'block',
    width: '100%',
    padding: '0.85rem 1rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    border: 'none',
    fontSize: '0.88rem',
    textAlign: 'center',
    color: '#fff',
    backgroundColor: 'rgba(167, 139, 250, 0.18)',
    cursor: 'pointer',
    flexShrink: 0,
    fontFamily: 'inherit',
    transition: 'background-color 0.15s'
  },
  errorBanner: {
    padding: '0.5rem 0.85rem',
    margin: '0.5rem 1rem',
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    border: '1px solid rgba(239, 68, 68, 0.4)',
    borderRadius: '8px',
    fontSize: '0.8rem'
  },
  shellRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    height: '100%',
    minHeight: 0,
    width: '100%'
  },
  chatColumn: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  sidePanel: {
    width: '300px',
    flexShrink: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    color: '#fff'
  },
  sidePanelHeader: {
    padding: '0.75rem 1rem',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    fontSize: '0.8rem',
    opacity: 0.7,
    fontWeight: 500,
    flexShrink: 0
  },
  sidePanelList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem'
  },
  sideItem: {
    textAlign: 'left',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
    padding: '0.55rem 0.7rem',
    fontSize: '0.82rem',
    lineHeight: 1.4,
    color: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background-color 0.12s, border-color 0.12s'
  },
  chipsRow: {
    display: 'flex',
    gap: '6px',
    padding: '0.5rem 0.75rem 0',
    overflowX: 'auto',
    flexShrink: 0,
    scrollbarWidth: 'none'
  },
  chip: {
    flexShrink: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '16px',
    padding: '0.4rem 0.75rem',
    fontSize: '0.8rem',
    color: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    maxWidth: '260px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    zIndex: 2000
  },
  modalCard: {
    width: '100%',
    maxWidth: '460px',
    maxHeight: '90vh',
    overflowY: 'auto',
    backgroundColor: '#0f0f12',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '14px',
    color: '#fff',
    padding: '1.25rem',
    fontFamily: 'inherit'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1rem'
  },
  modalTitle: { fontSize: '1.05rem', fontWeight: 600 },
  modalClose: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit'
  },
  field: { display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1rem' },
  fieldLabel: { fontSize: '0.85rem', fontWeight: 500 },
  fieldHint: { fontSize: '0.72rem', opacity: 0.55, lineHeight: 1.4 },
  select: {
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    color: '#fff',
    padding: '0.5rem 0.65rem',
    fontSize: '0.88rem',
    outline: 'none',
    fontFamily: 'inherit',
    appearance: 'auto'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '0.5rem'
  },
  btnPrimary: {
    backgroundColor: '#6366f1',
    border: 'none',
    borderRadius: '8px',
    color: '#fff',
    padding: '0.5rem 1rem',
    fontSize: '0.88rem',
    cursor: 'pointer',
    fontFamily: 'inherit'
  },
  btnSecondary: {
    backgroundColor: 'transparent',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px',
    color: '#fff',
    padding: '0.5rem 1rem',
    fontSize: '0.88rem',
    cursor: 'pointer',
    fontFamily: 'inherit'
  }
};

// Animations are intentionally omitted in v1. Injecting keyframes via either
// document.head.appendChild or JSX <style> has been a source of React 18
// removeChild errors in this app. Revisit once the tree is stable.

function initialOfName(name) {
  if (!name) return '?';
  return name.trim().charAt(0).toUpperCase();
}

// Parse @mentions against known participant names. Returns indices of matched personas.
// Longest names matched first so "@Persona 1" doesn't match "@Persona" when both exist.
function parseMentions(text, participants) {
  if (!text || !participants || participants.length === 0) return [];
  const sorted = [...participants].sort((a, b) => (b.personaName || '').length - (a.personaName || '').length);
  const indices = [];
  let remaining = text;
  for (const p of sorted) {
    if (!p.personaName) continue;
    const escaped = p.personaName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`@${escaped}(?:さん|san)?(?=[\\s,、。！？]|$)`, 'gi');
    if (pattern.test(remaining)) {
      if (!indices.includes(p.personaIndex)) indices.push(p.personaIndex);
      remaining = remaining.replace(pattern, '');
    }
  }
  return indices;
}

function ParticipantAvatar({ p, size = 'lg' }) {
  const wrapStyle = size === 'lg'
    ? { ...styles.avatarImgWrap, borderColor: p.color || '#fff' }
    : { ...styles.personaSmallAvatar, backgroundColor: p.color || '#444' };
  return (
    <div style={wrapStyle}>
      {p.imageUrl
        ? <img src={p.imageUrl} alt={p.personaName} style={styles.avatarImg} />
        : <span>{initialOfName(p.personaName)}</span>}
    </div>
  );
}

function TypingIndicator({ persona, thinkingLabel }) {
  return (
    <div style={styles.personaRow}>
      <ParticipantAvatar p={persona} size="sm" />
      <div style={styles.personaMeta}>
        <span style={styles.personaName}>{persona.personaName}</span>
        <div style={styles.typingText}>{thinkingLabel}</div>
      </div>
    </div>
  );
}

function ChatSettingsModal({ t, initial, personaCount, onSave, onClose, saving, error }) {
  const [mode, setMode] = useState(initial.responseMode);
  const [max, setMax] = useState(initial.maxRespondents);
  const [depth, setDepth] = useState(initial.responseDepth);
  const [tone, setTone] = useState(initial.responseTone);
  const [speed, setSpeed] = useState(initial.revealSpeed || 'normal');

  const maxCap = Math.min(10, Math.max(1, personaCount || 5));
  const boundedMax = Math.min(max, maxCap);

  const handleSave = () => onSave({ responseMode: mode, maxRespondents: boundedMax, responseDepth: depth, responseTone: tone, revealSpeed: speed });

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <div style={styles.modalOverlay} onClick={onClose} role="dialog" aria-modal="true">
      <div style={styles.modalCard} onClick={stopPropagation}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>{t('personaChat.settings.title')}</div>
          <button style={styles.modalClose} onClick={onClose} aria-label={t('personaChat.settings.cancel')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={styles.field}>
          <label style={styles.fieldLabel}>{t('personaChat.settings.responseMode')}</label>
          <select
            style={styles.select}
            value={mode}
            onChange={(e) => {
              const next = e.target.value;
              setMode(next);
              // 'all' overrides the max-respondents cap on the server, so mirror that
              // in the UI: lock the count to everyone in the group.
              if (next === 'all') setMax(maxCap);
            }}
          >
            <option value="smart">{t('personaChat.settings.mode.smart')}</option>
            <option value="random">{t('personaChat.settings.mode.random')}</option>
            <option value="all">{t('personaChat.settings.mode.all')}</option>
          </select>
          <span style={styles.fieldHint}>{t('personaChat.settings.responseModeHint')}</span>
        </div>

        <div style={styles.field}>
          <label style={{ ...styles.fieldLabel, opacity: mode === 'all' ? 0.55 : 1 }}>
            {t('personaChat.settings.maxRespondents')}
          </label>
          <select
            style={{ ...styles.select, opacity: mode === 'all' ? 0.55 : 1, cursor: mode === 'all' ? 'not-allowed' : 'pointer' }}
            value={boundedMax}
            disabled={mode === 'all'}
            onChange={(e) => setMax(Number(e.target.value))}
          >
            {Array.from({ length: maxCap }, (_, i) => i + 1).map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span style={styles.fieldHint}>{t('personaChat.settings.maxRespondentsHint')}</span>
        </div>

        <div style={styles.field}>
          <label style={styles.fieldLabel}>{t('personaChat.settings.responseDepth')}</label>
          <select style={styles.select} value={depth} onChange={(e) => setDepth(e.target.value)}>
            <option value="concise">{t('personaChat.settings.depth.concise')}</option>
            <option value="balanced">{t('personaChat.settings.depth.balanced')}</option>
            <option value="detailed">{t('personaChat.settings.depth.detailed')}</option>
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.fieldLabel}>{t('personaChat.settings.responseTone')}</label>
          <select style={styles.select} value={tone} onChange={(e) => setTone(e.target.value)}>
            <option value="conversational">{t('personaChat.settings.tone.conversational')}</option>
            <option value="professional">{t('personaChat.settings.tone.professional')}</option>
            <option value="debate">{t('personaChat.settings.tone.debate')}</option>
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.fieldLabel}>{t('personaChat.settings.revealSpeed')}</label>
          <select style={styles.select} value={speed} onChange={(e) => setSpeed(e.target.value)}>
            <option value="slow">{t('personaChat.settings.speed.slow')}</option>
            <option value="normal">{t('personaChat.settings.speed.normal')}</option>
            <option value="fast">{t('personaChat.settings.speed.fast')}</option>
            <option value="instant">{t('personaChat.settings.speed.instant')}</option>
          </select>
        </div>

        {error && <div style={{ ...styles.errorBanner, margin: '0 0 0.75rem 0' }}>{error}</div>}

        <div style={styles.modalFooter}>
          <button style={styles.btnSecondary} onClick={onClose} disabled={saving}>
            {t('personaChat.settings.cancel')}
          </button>
          <button
            style={{ ...styles.btnPrimary, opacity: saving ? 0.6 : 1, cursor: saving ? 'wait' : 'pointer' }}
            onClick={handleSave}
            disabled={saving}
          >
            {t('personaChat.settings.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Message({ msg, participants, revealState }) {
  if (msg.role === 'user') {
    return (
      <div style={styles.userRow}>
        <div style={styles.userBubble}>{msg.content}</div>
      </div>
    );
  }
  const p = participants.find(pp => pp.personaIndex === msg.personaIndex)
    || { personaName: msg.personaName, color: '#888' };

  // If this message has an active reveal entry, slice content to the revealed length.
  // Historical / loaded-from-server messages have no _revealId and render fully.
  const fullContent = msg.content || '';
  const revealedCount = msg._revealId != null ? revealState[msg._revealId] : undefined;
  const isRevealing = typeof revealedCount === 'number' && revealedCount < fullContent.length;
  const displayContent = isRevealing ? fullContent.slice(0, revealedCount) : fullContent;

  return (
    <div style={styles.personaRow}>
      <ParticipantAvatar p={p} size="sm" />
      <div style={styles.personaMeta}>
        <span style={styles.personaName}>{msg.personaName}</span>
        <div style={styles.personaBubble}>
          {displayContent}
          {isRevealing && <span style={styles.cursor}>▌</span>}
        </div>
      </div>
    </div>
  );
}

const FastResearchChat = ({ researchId, language }) => {
  const { t, i18n } = useTranslation('fastresearch');
  const navigate = useNavigate();
  const [participants, setParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [access, setAccess] = useState(null);
  const [input, setInput] = useState('');
  const [autoSendCountdown, setAutoSendCountdown] = useState(0);
  const autoSendTimer = useRef(null);
  const autoSendInterval = useRef(null);
  const handleSendRef = useRef(null);
  // @mention picker state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [sending, setSending] = useState(false);
  const [thinkingIndices, setThinkingIndices] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const messagesEndRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const textareaRef = useRef(null);
  const mountedRef = useRef(true);
  // Character-by-character reveal state, keyed by per-message revealId
  const [revealState, setRevealState] = useState({}); // { [id]: revealed:number }
  const revealTimers = useRef({});
  const isDesktop = useIsDesktop();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Clear all in-flight reveal timers on unmount to avoid setState-after-unmount
      Object.values(revealTimers.current).forEach((t) => clearTimeout(t));
      revealTimers.current = {};
    };
  }, []);

  // Character-by-character reveal with sentence-boundary pauses.
  // Returns a Promise that resolves when the reveal completes — handleSend
  // awaits this so personas type out sequentially (one at a time), matching
  // the in-app PersonaChat behavior.
  const startReveal = (id, content, speed = 'normal') => {
    if (!content) return Promise.resolve();
    const totalChars = content.length;

    if (speed === 'instant') {
      setRevealState((prev) => ({ ...prev, [id]: totalChars }));
      return Promise.resolve();
    }

    const speedConf = {
      slow:   { mult: 18, minMs: 3000, maxMs: 12000, pauseMin: 500, pauseRange: 600 },
      normal: { mult: 12, minMs: 1500, maxMs: 8000,  pauseMin: 350, pauseRange: 400 },
      fast:   { mult: 6,  minMs: 800,  maxMs: 4000,  pauseMin: 150, pauseRange: 200 },
    }[speed] || { mult: 12, minMs: 1500, maxMs: 8000, pauseMin: 350, pauseRange: 400 };

    const tickInterval = 30;
    const targetDuration = Math.max(speedConf.minMs, Math.min(speedConf.maxMs, totalChars * speedConf.mult));
    const charsPerTick = Math.max(1, Math.ceil(totalChars / (targetDuration / tickInterval)));

    // Sentence boundaries → brief pause when revealing crosses them
    const pausePositions = new Set();
    for (let i = 0; i < content.length; i++) {
      if ((content[i] === '.' || content[i] === '!' || content[i] === '?') &&
          i + 1 < content.length && content[i + 1] === ' ') {
        pausePositions.add(i + 2);
      }
      if (content[i] === '—') pausePositions.add(i + 1);
    }

    setRevealState((prev) => ({ ...prev, [id]: 0 }));

    return new Promise((resolve) => {
      let count = 0;
      const tick = () => {
        if (!mountedRef.current) { resolve(); return; }
        const prevCount = count;
        count = Math.min(count + charsPerTick, totalChars);

        let pauseAt = -1;
        for (const p of pausePositions) {
          if (p > prevCount && p <= count) { pauseAt = p; break; }
        }
        if (pauseAt >= 0) {
          count = pauseAt;
          pausePositions.delete(pauseAt);
        }

        setRevealState((prev) => ({ ...prev, [id]: count }));

        if (count >= totalChars) {
          delete revealTimers.current[id];
          resolve();
          return;
        }
        const pauseDuration = speedConf.pauseMin + Math.random() * speedConf.pauseRange;
        revealTimers.current[id] = setTimeout(tick, pauseAt >= 0 ? pauseDuration : tickInterval);
      };
      revealTimers.current[id] = setTimeout(tick, tickInterval);
    });
  };

  const currentLanguage = language || i18n.language || 'en';

  // Initial load: pulls everything (messages, participants, settings, access).
  // Polling: only refreshes access/settings — never messages, since the
  // sequential reveal loop in handleSend mutates `messages` over several
  // seconds and a poll-driven setMessages mid-loop would clobber it (and
  // worse: cause the loop's next setMessages([...prev, r]) to duplicate
  // a response that was already pulled in via the poll).
  const loadChat = useCallback(async (isInitial = false) => {
    try {
      const res = await axios.get(`/fast-research/${researchId}/chat`);
      if (!mountedRef.current) return;
      if (res.data.status === 'success') {
        if (isInitial) {
          setParticipants(res.data.data.participants || []);
          setMessages(res.data.data.messages || []);
          setSuggestedQuestions(res.data.data.suggestedQuestions || []);
        }
        if (res.data.data.settings) setSettings(res.data.data.settings);
        setAccess(res.data.data.access);
        setError('');
      }
    } catch (err) {
      if (!mountedRef.current) return;
      if (isInitial) {
        setError(err.response?.data?.message || t('personaChat.loadError', 'Couldn\'t load chat'));
      }
    } finally {
      if (mountedRef.current && isInitial) setLoading(false);
    }
  }, [researchId, t]);

  useEffect(() => {
    if (!researchId) return;
    loadChat(true);
    // Re-poll access so the UI reacts when the pipeline completes mid-chat
    const iv = setInterval(() => loadChat(false), ACCESS_POLL_MS);
    return () => clearInterval(iv);
  }, [researchId, loadChat]);

  // Always pin to bottom unless the user has manually scrolled up.
  // userScrolledUpRef is set/cleared by the onScroll handler on the
  // messages div (see render). When false, every update scrolls to bottom.
  useEffect(() => {
    if (userScrolledUpRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, thinkingIndices, revealState, suggestedQuestions]);

  const handleSend = async () => {
    cancelAutoSend(); // any send (manual or timed) clears pending auto-send
    const text = input.trim();
    if (!text || sending || !access?.allowed) return;

    const directedTo = parseMentions(text, participants);
    const isDirected = directedTo.length > 0;

    const userMsg = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
      isDirected,
      directedTo
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    // Clearing `input` state doesn't undo the inline height autoResize set —
    // reset the textarea to its base height so it shrinks after send.
    if (textareaRef.current) {
      textareaRef.current.style.height = '38px';
    }
    setSending(true);
    setError('');

    // Optimistic "thinking" indicators.
    // If the message @-mentions specific personas, show exactly those thinking.
    // Otherwise use settings to approximate the count.
    let picks;
    if (isDirected) {
      picks = directedTo;
    } else {
      const mode = settings?.responseMode || 'smart';
      const maxRespondents = Math.min(
        participants.length,
        Math.max(1, Number(settings?.maxRespondents) || 2)
      );
      if (mode === 'all') {
        picks = participants.map(p => p.personaIndex);
      } else {
        // smart / random: pick N random for visual activity (server picks the real N)
        const shuffled = [...participants];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        picks = shuffled.slice(0, maxRespondents).map(p => p.personaIndex);
      }
    }
    setThinkingIndices(picks);

    try {
      const res = await axios.post(`/fast-research/${researchId}/chat/message`, {
        message: text,
        language: currentLanguage,
        directedTo
      });
      if (!mountedRef.current) return;
      if (res.data.status === 'success') {
        const turnTs = Date.now();
        const responses = (res.data.data.responses || []).map((r, i) => ({
          ...r,
          timestamp: new Date().toISOString(),
          _revealId: `reveal-${turnTs}-${i}` // stable per-message id for the reveal map
        }));

        // Sequential reveal: typing indicator → add message (initialized empty)
        // → await character reveal → next persona's typing indicator.
        // Mirrors the in-app PersonaChat pattern.
        const speed = settings?.revealSpeed || 'normal';
        for (let i = 0; i < responses.length; i++) {
          if (!mountedRef.current) break;

          // Show only the upcoming persona as "thinking"
          setThinkingIndices([responses[i].personaIndex]);

          // Brief think pause before each persona starts (longer for the first)
          await new Promise(r => setTimeout(r, i === 0 ? 900 : 600));
          if (!mountedRef.current) break;

          // Add the message with reveal entry pre-initialized to 0 so it
          // renders empty (no flash of full content before reveal kicks in).
          const r = responses[i];
          setRevealState(prev => ({ ...prev, [r._revealId]: 0 }));
          setMessages(prev => [...prev, r]);
          // This persona is no longer "thinking" — they're typing
          setThinkingIndices([]);

          // Wait for character reveal to complete before moving on
          await startReveal(r._revealId, r.content || '', speed);
        }

        if (Array.isArray(res.data.data.suggestedQuestions)) {
          setSuggestedQuestions(res.data.data.suggestedQuestions);
        }
        // Background-only haptic nudge: only buzz if the user has the tab hidden
        // (looked away during the LLM wait). Foregrounded users get visual feedback only.
        if (
          responses.length > 0 &&
          typeof document !== 'undefined' &&
          document.visibilityState === 'hidden' &&
          typeof navigator !== 'undefined' &&
          typeof navigator.vibrate === 'function'
        ) {
          try { navigator.vibrate([20, 40, 20]); } catch (_) { /* ignore */ }
        }
      }
    } catch (err) {
      if (!mountedRef.current) return;
      if (err.response?.status === 402) {
        setAccess({ allowed: false, reason: err.response.data?.reason });
      } else if (err.response?.status === 400 && err.response.data?.reason === 'email_required') {
        setAccess({ allowed: false, reason: 'email_required' });
      } else {
        setError(err.response?.data?.message || t('personaChat.sendError', 'Couldn\'t send message'));
      }
    } finally {
      if (mountedRef.current) {
        setSending(false);
        setThinkingIndices([]);
      }
    }
  };

  // Keep the ref pointing at the latest handleSend so the auto-send timer
  // (set in startAutoSend) calls the current closure, not a stale one.
  handleSendRef.current = handleSend;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const autoResize = (e) => {
    // Any user typing/edit cancels the pending auto-send
    if (autoSendTimer.current) cancelAutoSend();
    const val = e.target.value;
    setInput(val);
    const el = textareaRef.current;
    if (el) {
      el.style.height = '38px';
      el.style.height = Math.min(el.scrollHeight, 88) + 'px';
    }
    // @-mention detection: show picker when "@" appears at start, after
    // whitespace, or after a non-ASCII char (CJK), and there's no space yet.
    const cursorPos = e.target.selectionStart;
    const textUpToCursor = val.substring(0, cursorPos);
    const lastAt = textUpToCursor.lastIndexOf('@');
    if (lastAt >= 0) {
      const after = textUpToCursor.substring(lastAt + 1);
      const charBefore = lastAt > 0 ? val[lastAt - 1] : ' ';
      const validPrefix = lastAt === 0 || /\s/.test(charBefore) || charBefore.charCodeAt(0) > 127;
      if (validPrefix && !/\s/.test(after)) {
        setShowMentionDropdown(true);
        setMentionFilter(after.toLowerCase());
        setMentionStartIndex(lastAt);
        return;
      }
    }
    setShowMentionDropdown(false);
  };

  const handleSelectMention = (participant) => {
    const before = input.substring(0, mentionStartIndex);
    const cursorPos = textareaRef.current?.selectionStart ?? input.length;
    const after = input.substring(cursorPos);
    const next = `${before}@${participant.personaName} ${after}`;
    setInput(next);
    setShowMentionDropdown(false);
    setTimeout(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        const newPos = before.length + 1 + participant.personaName.length + 1;
        el.setSelectionRange(newPos, newPos);
        el.style.height = '38px';
        el.style.height = Math.min(el.scrollHeight, 88) + 'px';
      }
    }, 0);
  };

  const filteredParticipants = participants.filter(p =>
    p.personaName.toLowerCase().includes(mentionFilter)
  );

  const handleSaveSettings = async (next) => {
    setSavingSettings(true);
    setSettingsError('');
    try {
      const res = await axios.patch(`/fast-research/${researchId}/chat/settings`, next);
      if (!mountedRef.current) return;
      if (res.data.status === 'success' && res.data.data?.settings) {
        setSettings(res.data.data.settings);
        setShowSettings(false);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setSettingsError(err.response?.data?.message || t('personaChat.settings.saveError', 'Couldn\'t save settings.'));
    } finally {
      if (mountedRef.current) setSavingSettings(false);
    }
  };

  // 3-second auto-send: clicking a suggested question pre-fills the input
  // and counts down for 3s. User can edit (cancels) or hit send (sends now).
  const cancelAutoSend = () => {
    if (autoSendTimer.current) { clearTimeout(autoSendTimer.current); autoSendTimer.current = null; }
    if (autoSendInterval.current) { clearInterval(autoSendInterval.current); autoSendInterval.current = null; }
    setAutoSendCountdown(0);
  };

  const startAutoSend = () => {
    cancelAutoSend();
    setAutoSendCountdown(3);
    let remaining = 3;
    autoSendInterval.current = setInterval(() => {
      remaining--;
      setAutoSendCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(autoSendInterval.current);
        autoSendInterval.current = null;
      }
    }, 1000);
    autoSendTimer.current = setTimeout(() => {
      setAutoSendCountdown(0);
      if (handleSendRef.current) handleSendRef.current();
    }, 3000);
  };

  // Cancel auto-send on unmount
  useEffect(() => () => cancelAutoSend(), []);

  const handleSuggestionClick = (q) => {
    setInput(q);
    // Focus the textarea and move caret to end so users can edit before sending
    setTimeout(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(q.length, q.length);
        el.style.height = '38px';
        el.style.height = Math.min(el.scrollHeight, 88) + 'px';
      }
      startAutoSend();
    }, 0);
  };

  const goToResults = () => navigate(`/ultrafastresearch/${researchId}/results`);

  const renderBlocked = () => {
    if (!access) return null;
    const textMap = {
      report_complete_upgrade_required: t('personaChat.reportComplete', 'Your report is ready. Upgrade to keep chatting for 7 days.'),
      free_window_expired: t('personaChat.windowExpired', 'Your free chat window has closed.'),
      access_expired: t('personaChat.accessExpired', 'Your chat access has expired.'),
      no_access_window: t('personaChat.noWindow', 'Chat access isn\'t active yet.'),
      email_required: t('personaChat.emailRequired', 'Submit your email to start chatting.')
    };
    const text = textMap[access.reason] || t('personaChat.blocked', 'Chat is not available right now.');
    // Actionable reasons route to the results page (teaser PDF + upgrade CTA live there).
    const actionable = ['report_complete_upgrade_required', 'free_window_expired', 'access_expired'];
    if (actionable.includes(access.reason)) {
      return (
        <button
          type="button"
          style={styles.blockedAction}
          onClick={goToResults}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(167, 139, 250, 0.3)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(167, 139, 250, 0.18)'; }}
        >
          {text} {t('personaChat.viewReport', 'View report →')}
        </button>
      );
    }
    return <div style={styles.blocked}>{text}</div>;
  };

  if (loading) {
    return (
      <div style={{ ...styles.container, alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ opacity: 0.6, fontSize: '0.85rem' }}>
          {t('personaChat.loading', 'Loading chat…')}
        </div>
      </div>
    );
  }

  const inputDisabled = sending || !access?.allowed;
  const showChips = !isDesktop && access?.allowed && suggestedQuestions.length > 0;
  const showSidePanel = isDesktop && suggestedQuestions.length > 0;

  const chatBlock = (
    <div style={styles.container}>
      {participants.length > 0 && (
        <div style={styles.participantsRow}>
          <div style={styles.participants}>
            {participants.map(p => (
              <div key={p.personaIndex} style={styles.avatar} title={p.personaName}>
                <ParticipantAvatar p={p} size="lg" />
                <span style={styles.avatarName}>{p.personaName}</span>
              </div>
            ))}
          </div>
          {settings && access?.allowed && (
            <button
              style={styles.settingsButton}
              onClick={() => { setSettingsError(''); setShowSettings(true); }}
              aria-label={t('personaChat.settings.open', 'Chat settings')}
              title={t('personaChat.settings.open', 'Chat settings')}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.09)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>
          )}
        </div>
      )}

      <div
        style={styles.messages}
        ref={scrollRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          // If the user is within ~40px of the bottom, treat them as "at bottom"
          // and resume auto-scrolling. Anything further up = they're reading.
          const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
          userScrolledUpRef.current = distanceFromBottom > 40;
        }}
      >
        {messages.length === 0 && thinkingIndices.length === 0 && (
          <div style={styles.empty}>
            {t('personaChat.emptyState', 'Chat with your personas while your report is being prepared. Ask them anything about their preferences, habits, or reactions.')}
          </div>
        )}
        {messages.map((msg, i) => (
          <Message
            key={msg._id || msg._revealId || `${msg.timestamp}-${i}`}
            msg={msg}
            participants={participants}
            revealState={revealState}
          />
        ))}
        {thinkingIndices.map(idx => {
          const p = participants.find(pp => pp.personaIndex === idx);
          if (!p) return null;
          return (
            <TypingIndicator
              key={`typing-${idx}`}
              persona={p}
              thinkingLabel={t('personaChat.thinking', 'thinking…')}
            />
          );
        })}
        <div ref={messagesEndRef} aria-hidden="true" />
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {access?.allowed ? (
        <>
          {showChips && (
            <div style={styles.chipsRow}>
              {suggestedQuestions.map((q, i) => (
                <button
                  key={`chip-${i}`}
                  style={styles.chip}
                  onClick={() => handleSuggestionClick(q)}
                  title={q}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          <div style={{ ...styles.inputRow, position: 'relative' }}>
            {/* @-mention picker */}
            {showMentionDropdown && filteredParticipants.length > 0 && (
              <div style={{
                position: 'absolute',
                bottom: 'calc(100% + 6px)',
                left: 0,
                right: 0,
                maxHeight: '200px',
                overflowY: 'auto',
                background: 'rgba(20, 18, 36, 0.96)',
                border: '1px solid rgba(167, 139, 250, 0.35)',
                borderRadius: '10px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(6px)',
                padding: '4px',
                zIndex: 50
              }}>
                {filteredParticipants.map(p => (
                  <button
                    key={p.personaIndex}
                    onClick={() => handleSelectMention(p)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      textAlign: 'left',
                      color: '#fff'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(99, 102, 241, 0.15)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{
                      width: '8px', height: '8px', borderRadius: '50%',
                      background: p.color || '#a78bfa', flexShrink: 0
                    }} />
                    <span style={{ fontWeight: 600 }}>{p.personaName}</span>
                  </button>
                ))}
              </div>
            )}
            <textarea
              ref={textareaRef}
              style={styles.textarea}
              value={input}
              onChange={autoResize}
              onKeyDown={handleKeyDown}
              placeholder={t('personaChat.placeholder', 'Ask your personas anything…')}
              disabled={inputDisabled}
              rows={1}
              maxLength={2000}
            />
            <button
              style={{
                ...styles.sendBtn,
                ...(inputDisabled || !input.trim() ? styles.sendBtnDisabled : {}),
                ...(autoSendCountdown > 0 ? { minWidth: '52px', fontWeight: 700 } : {})
              }}
              onClick={handleSend}
              disabled={inputDisabled || !input.trim()}
              aria-label={t('personaChat.send', 'Send')}
            >
              {autoSendCountdown > 0 ? (
                <span style={{ fontSize: '0.875rem', color: '#fff' }}>{autoSendCountdown}s</span>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              )}
            </button>
          </div>
        </>
      ) : (
        renderBlocked()
      )}
    </div>
  );

  const settingsModal = showSettings && settings && (
    <ChatSettingsModal
      t={t}
      initial={settings}
      personaCount={participants.length}
      saving={savingSettings}
      error={settingsError}
      onSave={handleSaveSettings}
      onClose={() => setShowSettings(false)}
    />
  );

  if (!showSidePanel) {
    return (<>{chatBlock}{settingsModal}</>);
  }

  return (
    <>
      <div style={styles.shellRow}>
        <div style={styles.chatColumn}>{chatBlock}</div>
        <aside style={styles.sidePanel}>
          <div style={styles.sidePanelHeader}>
            {t('personaChat.suggestionsLabel', 'Suggested questions')}
          </div>
          <div style={styles.sidePanelList}>
            {suggestedQuestions.map((q, i) => (
              <button
                key={`side-${i}`}
                style={styles.sideItem}
                onClick={() => handleSuggestionClick(q)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
              >
                {q}
              </button>
            ))}
          </div>
        </aside>
      </div>
      {settingsModal}
    </>
  );
};

export default FastResearchChat;
